// /src/lib/protectedSupabase.js
// Protected Supabase client wrapper
// Intercepts all mutation operations and checks authentication

import { createClient } from '@supabase/supabase-js';
import { authStorage } from './authStorage';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Singleton pattern to prevent multiple clients during hot module replacement
// Store client on window to persist across module reloads
const GLOBAL_KEY = '__OURNET_SUPABASE_CLIENT__';

let supabaseRaw;

if (typeof window !== 'undefined' && window[GLOBAL_KEY]) {
  console.log('🔌 protectedSupabase: Reusing existing Supabase client');
  supabaseRaw = window[GLOBAL_KEY];
} else {
  console.log('🔌 protectedSupabase: Creating new Supabase client');
  supabaseRaw = createClient(supabaseUrl, supabaseKey, {
    auth: {
      storage: authStorage,
      persistSession: true,
      // 2026-09-25 (docs/briefs/2026-09-25-single-token-refresher.md, ruling 1): ONE REFRESHER.
      // The hand-rolled single-flight path in src/lib/supabase.js (getAuthToken /
      // _refreshAuthToken) owns token refreshing. Before this, two refreshers shared one rotating
      // refresh token with no lock between them and both deleted the stored session on a 400 —
      // observed live on 15 Sep 2026 as two concurrent grant_type=refresh_token POSTs, both 400
      // (docs/briefs/2026-09-15-registration-confirmation-flow-probe-RESULTS.md:99-102).
      //
      // WHAT THIS FLAG ACTUALLY DISABLES, in auth-js 2.89.0 — three of the four refresh triggers:
      //   - the 30s background ticker          (GoTrueClient.js:2100-2103, gated at :2253/:2276)
      //   - the visibility-change refresh      (gated at :2229)
      //   - the boot recovery refresh          (gated at :1909, inside _recoverAndRefresh)
      // WHAT IT DOES *NOT* DISABLE — the lazy refresh inside __loadSession (:1226) is UNGATED, so
      // any client call that reads the session (getSession, _getUser, _updateUser, _signOut, and
      // every client-routed Storage request via _getAccessToken) still refreshes when the token is
      // within auth-js's 90s EXPIRY_MARGIN_MS, and still calls _removeSession() on a non-retryable
      // error (:1981). Narrowed, not closed. Two mitigations carry the rest:
      //   1. getAuthToken's proactive margin is 150s — wider than 90s — so storage normally already
      //      holds a rotated session by the time the client's threshold opens;
      //   2. _refreshAuthToken recovers from a rotation collision instead of clearing.
      // The residual and the remaining client-routed Storage calls are in
      // docs/briefs/2026-09-25-single-token-refresher-ledger.md. Do not set this back to true
      // without reading it: true is what caused the sign-outs.
      autoRefreshToken: false,
      detectSessionInUrl: false
    }
  });
  if (typeof window !== 'undefined') {
    window[GLOBAL_KEY] = supabaseRaw;
  }
}

export { supabaseRaw };

// Auth state reference - configured by AuthProvider
let authStateRef = {
  isLoggedIn: false,
  currentUserId: null,
  showAuthModal: () => {
    console.warn('AuthContext not yet initialized - auth modal not available');
  }
};

// Configure auth state (called from AuthContext useEffect)
export const configureAuthState = (authState) => {
  authStateRef = { ...authStateRef, ...authState };
};

// Check if mutation should be allowed
const shouldAllowMutation = () => {
  // Only allow mutations if user is actually logged in
  // Note: Test user ID is used for browsing fallback, NOT for write permission
  if (authStateRef.isLoggedIn) {
    return true;
  }

  // Block mutation for unauthenticated users
  return false;
};

// Create blocked response matching Supabase error format
const createBlockedResponse = () => ({
  data: null,
  error: {
    message: 'Authentication required',
    code: 'AUTH_REQUIRED',
    details: null,
    hint: 'Please log in to perform this action'
  }
});

// Create a chainable blocked response (maintains Supabase API pattern)
const createChainableBlockedResponse = () => {
  const blockedResponse = createBlockedResponse();

  // Create a thenable that resolves to blocked response
  const chainable = {
    ...blockedResponse,
    select: () => chainable,
    single: () => Promise.resolve(blockedResponse),
    maybeSingle: () => Promise.resolve(blockedResponse),
    order: () => chainable,
    limit: () => chainable,
    range: () => chainable,
    eq: () => chainable,
    neq: () => chainable,
    gt: () => chainable,
    gte: () => chainable,
    lt: () => chainable,
    lte: () => chainable,
    like: () => chainable,
    ilike: () => chainable,
    is: () => chainable,
    in: () => chainable,
    contains: () => chainable,
    containedBy: () => chainable,
    filter: () => chainable,
    match: () => chainable,
    not: () => chainable,
    or: () => chainable,
    then: (resolve) => resolve(blockedResponse),
    catch: (reject) => Promise.resolve(blockedResponse).catch(reject),
    finally: (callback) => Promise.resolve(blockedResponse).finally(callback)
  };

  return chainable;
};

// Wrap a single mutation method with auth check
const wrapMutationMethod = (originalMethod, methodName) => {
  return function(...args) {
    if (!shouldAllowMutation()) {
      console.log(`Protected Supabase: Blocking ${methodName} - user not authenticated`);
      authStateRef.showAuthModal('gate_body_generic');
      return createChainableBlockedResponse();
    }
    return originalMethod.apply(this, args);
  };
};

// SIMPLIFIED: Pass through to raw client directly
// The Proxy/Object.create approaches were causing hangs with concurrent queries
// TODO: Re-implement mutation protection at service/component level if needed
export const supabase = supabaseRaw;

// For debugging/testing
export const getAuthState = () => ({ ...authStateRef });
